import React, { Component } from "react";
import axios from "axios";
import { Navigate } from "react-router-dom";

import Constants from "Helpers/Constants";
import init from "Helpers/WindowToken";
import {
  apiErrorMessage,
  firstError,
  hasErrors,
  validateSitePage,
} from "Helpers/ContentValidation";
import {
  emptyPageForm,
  pageFormToPayload,
  pageToFormValues,
} from "Helpers/ContentPayloads";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

import { Card, Divider, Grid } from "@mui/material";

import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDSnackbar from "components/MDSnackbar";
import MDTypography from "components/MDTypography";

import PageFormFields from "components/ContentForms/PageFormFields";
import SectionEditor from "components/ContentForms/SectionEditor";

export default class UpdateSitePage extends Component {
  state = {
    id: "",
    values: emptyPageForm(),
    errors: {},

    fetching: true,
    loading: false,
    notification: false,
    notiColor: "success",
    message: "",
    redirect: false,
  };

  componentDidMount() {
    const id = window.location.pathname.split("/")[2];

    if (!id) {
      this.setState({ fetching: false });
      return this.notify("warning", "No page selected");
    }

    this.setState({ id }, () => this.fetchPage(id));
  }

  fetchPage = async (id) => {
    if (init() !== "success") return this.setState({ redirect: true });

    try {
      const response = await axios.get(Constants.getUrls.getSitePage + id);
      const page = response.data?.data;

      if (!page) throw new Error("Page not found");

      this.setState({ values: pageToFormValues(page), fetching: false });
    } catch (error) {
      this.setState({ fetching: false });
      this.notify("warning", apiErrorMessage(error, "Could not load the page"));
    }
  };

  handleChange = (name, value) => {
    this.setState((state) => {
      const errors = { ...state.errors };
      delete errors[name];
      return { values: { ...state.values, [name]: value }, errors };
    });
  };

  handleSectionsChange = (sections) => {
    this.setState((state) => ({ values: { ...state.values, sections } }));
  };

  notify = (notiColor, message) => {
    this.setState({ notification: true, notiColor, message });
    setTimeout(() => this.setState({ notification: false, message: "" }), 4000);
  };

  /**
   * Saves without leaving the screen — a page with many blocks is usually
   * edited in several passes.
   */
  handleSubmit = async (event, { close = false } = {}) => {
    event.preventDefault();

    const errors = validateSitePage(this.state.values, { isUpdate: true });
    if (hasErrors(errors)) {
      this.setState({ errors });
      return this.notify("warning", firstError(errors));
    }

    if (init() !== "success") return this.setState({ redirect: true });

    this.setState({ loading: true, errors: {} });

    try {
      const response = await axios.put(
        Constants.putUrls.updateSitePage + this.state.id,
        pageFormToPayload(this.state.values)
      );

      this.setState({ loading: false });
      this.notify("success", response.data?.message || "Page updated successfully!");

      if (close) setTimeout(() => this.setState({ redirect: true }), 1200);
    } catch (error) {
      this.setState({ loading: false });
      this.notify("warning", apiErrorMessage(error, "Could not update the page"));
    }
  };

  render() {
    if (this.state.redirect) return <Navigate to="/sitepages" replace />;

    return (
      <DashboardLayout>
        <DashboardNavbar />

        <MDBox pt={6} pb={3}>
          <Card>
            <MDBox
              mx={2}
              mt={-3}
              py={3}
              px={2}
              variant="gradient"
              bgColor="info"
              borderRadius="lg"
              coloredShadow="info"
            >
              <MDTypography variant="h6" color="white">
                Update Website Page
              </MDTypography>
              {this.state.values.name ? (
                <MDTypography variant="caption" color="white">
                  {this.state.values.name} · {this.state.values.page_route ||
                    `/${this.state.values.slug}`}
                </MDTypography>
              ) : null}
            </MDBox>

            <MDBox py={3} px={2}>
              {this.state.fetching ? (
                <Grid align="center">
                  <h4>Loading...</h4>
                </Grid>
              ) : (
                <form onSubmit={(event) => this.handleSubmit(event, { close: true })} noValidate>
                  <PageFormFields
                    values={this.state.values}
                    errors={this.state.errors}
                    onChange={this.handleChange}
                    isUpdate
                  />

                  <MDBox mt={3} mb={2}>
                    <Divider />
                  </MDBox>

                  <SectionEditor
                    sections={this.state.values.sections}
                    onChange={this.handleSectionsChange}
                    errors={this.state.errors}
                    onError={(message) => this.notify("warning", message)}
                  />

                  <MDBox mt={4} display="flex" gap={2}>
                    <MDButton
                      type="submit"
                      variant="gradient"
                      color="info"
                      disabled={this.state.loading}
                    >
                      {this.state.loading ? "Saving..." : "Save & Close"}
                    </MDButton>

                    <MDButton
                      variant="outlined"
                      color="info"
                      onClick={(event) => this.handleSubmit(event, { close: false })}
                      disabled={this.state.loading}
                    >
                      Save
                    </MDButton>

                    <MDButton
                      variant="outlined"
                      color="dark"
                      onClick={() => this.setState({ redirect: true })}
                      disabled={this.state.loading}
                    >
                      Cancel
                    </MDButton>
                  </MDBox>
                </form>
              )}
            </MDBox>

            <MDSnackbar
              color={this.state.notiColor}
              icon="notifications"
              title="Website Pages"
              content={this.state.message}
              dateTime="Just now"
              open={this.state.notification}
              close={() => this.setState({ notification: false })}
            />
          </Card>
        </MDBox>
      </DashboardLayout>
    );
  }
}
